import React, { useState, useEffect, useRef } from 'react';
import { usePharmacyPickerContext } from './PharmacyPickerContext';
import { APIProvider, Map, MapControl, ControlPosition, InfoWindow } from '@vis.gl/react-google-maps';
import { PharmacyDetailsModal } from './PharmacyDetailsModal';
import { MapControls } from './MapControls';
import { PharmacyMarkers } from './PharmacyMarkers';
import { clientConfig } from '../../config';
import useIsMobile from '../../customHooks/useIsMobile';
import './PharmacyMap.css';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { useCustomer } from '../../customHooks/useCustomer';
import { useCartContext } from '../../customHooks/useCart';

type LatLng = { lat: number; lng: number };

interface PharmacyMapProps {
  children?: React.ReactNode;
  allPharmaciesLocations: { [stockCode: string]: { name: string, address: string; location?: LatLng; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; } };
  selectedStockCode?: string | null;
  onPharmacySelect?: (stockCode: string | null) => void;
  highlightedStockCode?: string | null;
  onPharmacyHighlight?: (stockCode: string | null) => void;
  onLocationUsed?: (userLocation: LatLng, nearbyPharmacies: Array<{ stockCode: string; name: string; address: string; location?: LatLng; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; distance: number }>) => void;
  height?: number;
  productsCountPerPharmacy?: { [stockCode: string]: number };
  showSelectButton?: boolean;
}

export const PharmacyMap: React.FC<PharmacyMapProps> = ({ 
  children, 
  allPharmaciesLocations, 
  selectedStockCode, 
  onPharmacySelect, 
  highlightedStockCode,
  onPharmacyHighlight,
  onLocationUsed,
  height,
  productsCountPerPharmacy = {},
  showSelectButton = true,
}) => {
  const language = localStorage.getItem("language") || "de";
  const { selectedService } = usePharmacyPickerContext();
  const { customerState } = useCustomer();
  const { getCart } = useCartContext();
  
  // Default to center of Berlin
  const [myLocation, setMyLocation] = useState<LatLng>({
    lat: 52.520008,
    lng: 13.404954
  });

  const isMobile = useIsMobile();

  const mapInstanceRef = useRef<any>(null);
  const hasInitialized = useRef(false);

  const mapHeight = height || (isMobile ? 360 : 480);
  
  // Modal state for pharmacy details
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState<{ name: string, address: string; location?: LatLng; is_external_pharmacy: boolean; opening_hours?: string; external_integration_code?: string; } | null>(null);
  const [currentModalStockCode, setCurrentModalStockCode] = useState<string | null>(null);

  // Fullscreen state
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
      setIsFullScreen(!!fullscreenEl);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle pharmacy selection and modal
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (selectedStockCode && map) {
      const pharmacy = allPharmaciesLocations[selectedStockCode];
      
      if (pharmacy?.location) {
        map.panTo(pharmacy.location);
        map.setZoom(15);
        setSelectedPharmacy(pharmacy);
        setCurrentModalStockCode(selectedStockCode);
        setIsModalOpen(true);
      }
    } else if (!selectedStockCode) {
      setIsModalOpen(false);
      setSelectedPharmacy(null);
      setCurrentModalStockCode(null);
    }
  }, [selectedStockCode, allPharmaciesLocations]);

  // Handle pharmacy highlighting (from search) - pan to highlighted pharmacy and show modal
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (highlightedStockCode && map && !selectedStockCode) {
      const pharmacy = allPharmaciesLocations[highlightedStockCode];
      if (pharmacy?.location) {
        map.panTo(pharmacy.location);
        map.setZoom(15);
        setSelectedPharmacy(pharmacy);
        setCurrentModalStockCode(highlightedStockCode);
        setIsModalOpen(true);
      }
    } else if (!highlightedStockCode && !selectedStockCode) {
      // Clear modal when highlight is cleared and nothing is selected
      setIsModalOpen(false);
      setSelectedPharmacy(null);
      setCurrentModalStockCode(null);
    }
  }, [highlightedStockCode, allPharmaciesLocations, selectedStockCode]);

  const handleClosePharmacyModal = () => {
    setIsModalOpen(false);
    setCurrentModalStockCode(null);
    // Clear highlight when modal closes
    if (highlightedStockCode && onPharmacyHighlight) {
      onPharmacyHighlight(null);
    }
    // Call onPharmacySelect(null) to notify parent that selection was cleared
    if (selectedStockCode && onPharmacySelect) {
      onPharmacySelect(null);
    }
  }

  const handleSelectPharmacy = () => {
    if (currentModalStockCode && onPharmacySelect) {
      const pharmacy = allPharmaciesLocations[currentModalStockCode];
      
      if (pharmacy) {
        const userId = customerState?.customerID || null;
        const cart = getCart();
        const userAddressAvailable = cart?.zipcode ? 'yes' : 'no';
        
        // Calculate distance if user location and pharmacy location are available
        let distance: number | null = null;
        if (myLocation && pharmacy.location) {
          const R = 6371; // Earth's radius in kilometers
          const dLat = ((pharmacy.location.lat - myLocation.lat) * Math.PI) / 180;
          const dLng = ((pharmacy.location.lng - myLocation.lng) * Math.PI) / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((myLocation.lat * Math.PI) / 180) *
              Math.cos((pharmacy.location.lat * Math.PI) / 180) *
              Math.sin(dLng / 2) *
              Math.sin(dLng / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          distance = R * c;
        }
        
        const productsAvailableCount = productsCountPerPharmacy[currentModalStockCode] || 0;
        
        // Check if geolocation is enabled
        const geoEnabled = navigator.geolocation ? 'yes' : 'no';
        
        // Track pharmacy selection (critical event - binds customer selection)
        MainTracker.track("custom", "pharmacy_selected", {
          user_id: userId,
          pharmacy_id: currentModalStockCode,
          pharmacy_name: pharmacy.name,
          distance_km: distance,
          products_available_count: productsAvailableCount,
          user_address_available: userAddressAvailable,
          geo_enabled: geoEnabled
        });
      }
      
      onPharmacySelect(currentModalStockCode);
      setIsModalOpen(false);
      // Clear highlight when selecting
      if (onPharmacyHighlight) {
        onPharmacyHighlight(null);
      }
    }
  }

  return (
    <APIProvider apiKey={clientConfig.GOOGLE_MAPS_API_KEY}>
      <Map
        id="main-map"
        mapId="3b74bbd4d17c2d4b68514c29"
        defaultZoom={13}
        defaultCenter={myLocation}
        mapTypeControl={false}
        mapTypeId={'roadmap'}
        streetViewControl={false}
        cameraControl={false}
        keyboardShortcuts={false}
        style={{ height: mapHeight, borderRadius: 8, overflow: 'hidden', position: 'relative' }}
      >
        <MapControls
          myLocation={myLocation}
          setMyLocation={setMyLocation}
          allPharmaciesLocations={allPharmaciesLocations}
          onLocationUsed={onLocationUsed}
          mapInstanceRef={mapInstanceRef}
          hasInitialized={hasInitialized}
          language={language}
          isPharmacyModalOpen={isModalOpen}
          onPharmacyModalClose={handleClosePharmacyModal}
        />
        
        <PharmacyMarkers
          allPharmaciesLocations={allPharmaciesLocations}
          selectedStockCode={selectedStockCode}
          highlightedStockCode={highlightedStockCode}
          onPharmacySelect={onPharmacySelect}
          onPharmacyHighlight={onPharmacyHighlight}
          mapInstanceRef={mapInstanceRef}
          setSelectedPharmacy={setSelectedPharmacy}
          setIsModalOpen={setIsModalOpen}
          setCurrentModalStockCode={setCurrentModalStockCode}
        />

        {children && (
          <MapControl position={ControlPosition.BOTTOM_CENTER}>
            <div
              style={{
                width: (isFullScreen && !isMobile) ? '1200px'
                    : window.innerWidth >= 768 ? '470px' : '370px',
              }}
            >
              {children}
            </div>
          </MapControl>
        )}

        {/* InfoWindow anchored to selected pharmacy location */}
        {isModalOpen && selectedPharmacy?.location && (
          <InfoWindow
            position={selectedPharmacy.location}
            onCloseClick={() => {
              setIsModalOpen(false);
              // Clear highlight when modal closes
              if (highlightedStockCode && onPharmacyHighlight) {
                onPharmacyHighlight(null);
              }
              // Call onPharmacySelect(null) to notify parent that selection was cleared
              if (selectedStockCode && onPharmacySelect) {
                onPharmacySelect(null);
              }
            }}
            headerDisabled={true}
            pixelOffset={[0, -50]}
          >
            <PharmacyDetailsModal
              isOpen={isModalOpen}
              onClose={handleClosePharmacyModal}
              onSelect={handleSelectPharmacy}
              selectedService={selectedService}
              showSelectButton={showSelectButton}
              pharmacy={selectedPharmacy}
            />
          </InfoWindow>
        )}

      </Map>
    </APIProvider>
  );
};

